"use client";

/**
 * FaqSection — discrete FAQ accordion for sector landing pages.
 *
 * Purpose : match the JSON-LD FAQPage schema (defined in the page layout)
 * with visible content on the page, so Google grants rich snippets
 * AND LLMs (ChatGPT, Perplexity, Claude) can cite the answers.
 *
 * Design intent : minimal visual footprint — sits at the bottom of the page,
 * above the CTA. All accordions closed by default, narrow container,
 * subtle background. Not meant to dominate the page.
 */

import { motion } from "motion/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export type FaqItem = {
  q: string;
  a: string;
};

type FaqSectionProps = {
  items: FaqItem[];
  title?: string;
  subtitle?: string;
};

export function FaqSection({ items, title, subtitle }: FaqSectionProps) {
  return (
    <section
      id="faq"
      className="border-t border-border/30 bg-card/10 py-16 sm:py-20"
    >
      <div className="container mx-auto max-w-3xl px-4">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="mb-8 text-center"
        >
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
            {title ?? "Questions fréquentes"}
          </h2>
          {subtitle ? (
            <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
          ) : null}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Accordion type="single" collapsible className="space-y-2">
            {items.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="rounded-xl border border-border/60 bg-card/30 px-4 transition-colors data-[state=open]:bg-card/60"
              >
                <AccordionTrigger className="text-left text-sm font-medium hover:no-underline sm:text-base">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {item.a}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
